// KpisCard — service-level position: last closed month's compliance,
// breaches and at-risk results, service credits, and reports outstanding.
//
// Service credits are RECORDED amounts. The KPI model's credit formula is
// display-only free text that is never evaluated, so an accrued figure cannot
// be derived — the card sums what people entered and labels the metric
// accordingly. An uncosted breach shows up as a breach with no matching
// credit rather than as a silent $0.

import MetricCard from "../tracking/deliverable/MetricCard";
import ReportSection, {
  NothingToReport,
  UnavailableNotice,
} from "./ReportSection";
import { countTone, formatPercent } from "./reportFormat";
import { formatAud2dp } from "../../../utils/formatCurrency";
import { formatMonthYear } from "../../../utils/formatShortDate";
import type {
  SectionUnavailable,
  SummaryKpisSection,
} from "../../../types/report";

const SECTION_LABEL = "KPIs and service levels";

export interface KpisCardProps {
  section: SummaryKpisSection | SectionUnavailable;
}

export default function KpisCard({ section }: KpisCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="kpis" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  if (section.kpiCount === 0) {
    return (
      <ReportSection id="kpis" title={SECTION_LABEL}>
        <NothingToReport />
      </ReportSection>
    );
  }

  const monthSubtitle =
    section.lastClosedMonth === null
      ? "No period has closed yet"
      : `${formatMonthYear(`${section.lastClosedMonth}-01`)} · ${section.lastMonthMet} of ${section.lastMonthMeasured} met`;

  const creditsSubtitle = `across ${section.creditsRecordedCount} months · ex-GST`;

  return (
    <ReportSection id="kpis" title={SECTION_LABEL}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard
          testId="kpis-last-month"
          label="Last closed month"
          value={formatPercent(section.lastMonthWeightedPct)}
          subtitle={monthSubtitle}
        />
        <MetricCard
          testId="kpis-breaches"
          label="Breaches"
          value={section.breachCount}
          valueTone={countTone(section.breachCount, "danger")}
        />
        <MetricCard
          testId="kpis-at-risk"
          label="At risk"
          value={section.atRiskCount}
          valueTone={countTone(section.atRiskCount, "warning")}
        />
        <MetricCard
          testId="kpis-credits"
          label="Service credits recorded"
          value={formatAud2dp(section.creditsRecordedTotal)}
          subtitle={creditsSubtitle}
        />
        <MetricCard
          testId="kpis-reports-outstanding"
          label="Reports outstanding"
          value={section.reportsOutstanding}
          valueTone={countTone(section.reportsOutstanding, "warning")}
          subtitle="closed periods with no result logged"
        />
      </div>
    </ReportSection>
  );
}
